import { format, isValid } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminActivityLog } from '../api';
import { ACCOUNT_ACTION_TYPE_LABEL } from '../types';

function formatActionTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isValid(d) ? format(d, 'MMM d, yyyy p') : iso;
}

function actionTypeLabel(code?: string): string {
  if (!code) return '—';
  return ACCOUNT_ACTION_TYPE_LABEL[code] ?? code;
}

/**
 * Activity Log section (CMP parity: AdminManagementDetailsScreen's "Activity Log" table, which was
 * wired to hardcoded mock rows there). Reads the real audit trail — GET
 * /admin/api/account/{adminId}/activity-log — actions performed BY this admin, newest first. The
 * write side (AdminServiceImpl.recordActionLog / recordActionLogForUpdateInfo) already existed;
 * this section is the first consumer of the read side.
 */
export function ActivityLogSection({ adminId }: { adminId: number }) {
  const query = useAdminActivityLog(adminId);
  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : query.isError ? (
          <p className="py-4 text-sm text-destructive">Failed to load activity log.</p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead>Actor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id ?? i}>
                  <TableCell className="whitespace-nowrap">{formatActionTime(r.actionTime)}</TableCell>
                  <TableCell className="whitespace-nowrap">{actionTypeLabel(r.actionType)}</TableCell>
                  <TableCell>{r.actionDetail || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {r.actorEmail || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
